<script setup lang="ts">
import { Head, Link, router } from '@inertiajs/vue3'
import { computed, inject, ref } from 'vue'
import PageHeading from '@/Components/Headings/PageHeading.vue'
import { PageHeading as PageHeadingTS } from '@/types/PageHeading'
import Table from '@/Components/Table/Table.vue'
import Icon from '@/Components/Utils/Icon.vue'
import Button from '@/Components/Utils/Button.vue'
import ModalConfirmationDelete from '@/Components/Utils/ModalConfirmationDelete.vue'
import { layoutStructure } from '@/Composables/useLayoutStructure'
import { faTrashAlt } from '@fal'
import { library } from '@fortawesome/fontawesome-svg-core'
library.add(faTrashAlt)
import { useFormatCurrency } from '@/Composables/useFormatCurrency'
import { useFormatTime } from '@/Composables/useFormatTime'

defineProps<{
    pageHead?: PageHeadingTS
    title?: string
    data: {}
    states: { value: string, label: string }[]
    printTypes: { value: string, label: string, icon: any }[]
    salesOptions: { value: string, label: string }[]
}>()

const params = new URLSearchParams(window.location.search)
const salesFilter = ref(params.get('filter[sales]') ?? '')
const monthFilter = ref(params.get('filter[month]') ?? '')
const stateFilter = ref(params.get('filter[state]') ?? '')
const printTypeFilter = ref(params.get('filter[print_type]') ?? '')

const applyFilters = () => {
    router.get(route('kasir.orders.index'), {
        'filter[sales]': salesFilter.value || undefined,
        'filter[month]': monthFilter.value || undefined,
        'filter[state]': stateFilter.value || undefined,
        'filter[print_type]': printTypeFilter.value || undefined,
    }, { preserveState: true, replace: true })
}

const togglePrintTypeFilter = (printType: string) => {
    printTypeFilter.value = printTypeFilter.value === printType ? '' : printType
    applyFilters()
}

const resetFilters = () => {
    salesFilter.value = ''
    monthFilter.value = ''
    stateFilter.value = ''
    printTypeFilter.value = ''
    applyFilters()
}

const layout = inject('layout', layoutStructure)
const isSuperAdmin = computed(() => layout?.auth?.role === 'super_admin')

const selectedRows = ref<Record<string, boolean>>({})
const selectedOrderIds = computed(() => Object.entries(selectedRows.value)
    .filter(([, isSelected]) => isSelected)
    .map(([id]) => Number(id)))

const tableKey = ref(0)
const onBulkDeleted = () => {
    selectedRows.value = {}
    tableKey.value++
}
</script>

<template>
    <Head :title="title" />
    <PageHeading :data="pageHead" />

    <div class="mx-4 mt-4 flex flex-wrap items-end gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4">
        <div>
            <label class="mb-1 block text-xs text-gray-500">Kasir</label>
            <select v-model="salesFilter" class="rounded-md border-gray-300 text-sm" @change="applyFilters">
                <option value="">Semua</option>
                <option v-for="sales in salesOptions" :key="sales.value" :value="sales.value">{{ sales.label }}</option>
            </select>
        </div>
        <div>
            <label class="mb-1 block text-xs text-gray-500">Bulan</label>
            <input v-model="monthFilter" type="month" class="rounded-md border-gray-300 text-sm" @change="applyFilters" />
        </div>
        <div>
            <label class="mb-1 block text-xs text-gray-500">Status</label>
            <select v-model="stateFilter" class="rounded-md border-gray-300 text-sm" @change="applyFilters">
                <option value="">Semua</option>
                <option v-for="state in states" :key="state.value" :value="state.value">{{ state.label }}</option>
            </select>
        </div>
        <div>
            <label class="mb-1 block text-xs text-gray-500">Jenis cetak</label>
            <select v-model="printTypeFilter" class="rounded-md border-gray-300 text-sm" @change="applyFilters">
                <option value="">Semua</option>
                <option v-for="printType in printTypes" :key="printType.value" :value="printType.value">{{ printType.label }}</option>
            </select>
        </div>
        <button class="pb-2 text-xs text-gray-500 underline hover:text-gray-700" @click="resetFilters">Reset filter</button>
    </div>

    <div v-if="isSuperAdmin && selectedOrderIds.length" class="mx-4 mt-4 flex items-center justify-between rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm">
        <span class="text-red-700">{{ selectedOrderIds.length }} pesanan dipilih</span>
        <ModalConfirmationDelete
            :routeDelete="{ name: 'kasir.orders.bulk-delete', parameters: { ids: selectedOrderIds }, method: 'delete' }"
            :title="`Hapus ${selectedOrderIds.length} pesanan?`"
            description="Pesanan terpilih beserta produk, pembayaran, dan produksinya akan dihapus permanen dan tidak bisa dikembalikan."
            yesLabel="Ya, hapus semua"
            yesIcon="fal fa-trash-alt"
            @onSuccess="onBulkDeleted"
        >
            <template #default="{ changeModel, isLoadingdelete }">
                <Button type="delete" size="xs" label="Hapus terpilih" icon="fal fa-trash-alt" :loading="isLoadingdelete" @click="() => changeModel()" />
            </template>
        </ModalConfirmationDelete>
    </div>

    <Table
        :key="tableKey"
        :resource="data"
        :isCheckBox="isSuperAdmin"
        checkboxKey="id"
        @onSelectRow="(rows: Record<string, boolean>) => selectedRows = { ...rows }"
    >
        <template #cell(reference)="{ item }">
            <Link :href="route('kasir.orders.show', item.slug)" class="primaryLink font-mono text-xs">
                {{ item.reference }}
            </Link>
        </template>

        <template #cell(brand_name)="{ item }">
            <Link v-if="item.brand_slug" :href="route('kasir.brands.show', item.brand_slug)" class="secondaryLink">
                {{ item.brand_name }}
                <Icon v-if="item.brand_status === 'eksklusif'" :data="item.brand_status_icon" />
            </Link>
            <div v-else />
        </template>

        <template #cell(print_types)="{ item }">
            <div class="flex flex-wrap gap-1">
                <button
                    v-for="printType in item.print_types"
                    :key="printType.value"
                    type="button"
                    class="inline-flex items-center gap-x-1 whitespace-nowrap rounded-md border px-2 py-0.5 text-xs"
                    :class="printTypeFilter === printType.value
                        ? 'border-cyan-500 bg-cyan-50 text-cyan-700'
                        : 'border-gray-200 text-gray-600 hover:bg-gray-50'"
                    v-tooltip="printTypeFilter === printType.value ? 'Hapus filter' : `Tampilkan hanya pesanan ${printType.label.toLowerCase()}`"
                    @click="togglePrintTypeFilter(printType.value)"
                >
                    <Icon :data="printType.icon" />
                    <span class="font-semibold tabular-nums">{{ printType.products_count }}</span>
                    {{ printType.label }}
                </button>
            </div>
        </template>

        <template #cell(state_label)="{ item }">
            <div class="flex items-center gap-x-1.5">
                <Icon v-if="item.state_icon" :data="item.state_icon" />
                <span>{{ item.state_label }}</span>
            </div>
        </template>

        <template #cell(invoice_date)="{ item }">
            {{ useFormatTime(item.invoice_date) }}
        </template>

        <template #cell(total_amount)="{ item }">
            {{ useFormatCurrency(item.total_amount) }}
        </template>

        <template #cell(remaining_amount)="{ item }">
            <span :class="item.remaining_amount > 0 ? 'text-red-500' : 'text-emerald-600'">
                {{ useFormatCurrency(item.remaining_amount) }}
            </span>
        </template>
    </Table>
</template>
